import { Card } from "@coherence-design-system/controls";
import { FluentProvider, OptionOnSelectData, webLightTheme } from "@fluentui/react-components";
import { CardContent } from "@mui/material";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../../BasicComponents/DisplayAPIsExceptionAlert";
import { logException } from "../../../helper/AppInsightsHelper";
import { IArrayKey } from "../../../interfaces/CarouselProps.interfaces";
import { Loader } from "../../../SharedComponents/Loader";
import { useGetFeatureDataForSecurityCopilotQuery } from "../../../slices/ExploreCustomers.slice";
import { arrSortDescByKeyLength } from "../../../Utils/js-common";
import CustomDropdown from "../../ProductView/Common/CustomDropdown";
import { CustomerFeatureChart } from "./CustomerFeatureChart";
import { FeatureDataViewProps } from "./FeatureDataView.types";

export default function FeatureDataViewForSecurityCopilot({ tenantId }: FeatureDataViewProps) {
  const [searchParams] = useSearchParams();
  const securityCopilotFeaturesQuery = useGetFeatureDataForSecurityCopilotQuery({ id: tenantId });

  const { data, isFetching, error } = securityCopilotFeaturesQuery;
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [aggregation, setAggregation] = useState<string>(searchParams.get("granularity") ?? "Monthly");
  const containerRef = useRef<HTMLDivElement>(null);
  const aggregationOptions = [
    { text: "Daily", value: "Daily" },
    { text: "Monthly", value: "Monthly" }
  ];

  const handleSelectedAggregation = ({ optionText }: OptionOnSelectData) => {
    setAggregation(optionText ?? aggregation);
  };

  useLayoutEffect(() => {
    const calculateResponsiveWidth = () => {
      if (containerRef.current) {
        // Calculate responsive width based on container
        containerRef.current.offsetWidth;
      }
    };
    calculateResponsiveWidth();
    window.addEventListener("resize", calculateResponsiveWidth);
    return () => window.removeEventListener("resize", calculateResponsiveWidth);
  }, []);

  useEffect(() => {
    if (isFetching) {
      setSortedData([]);
    } else if (error) {
      logException("Customer Page", "Features", "useGetFeatureDataForSecurityCopilotQuery", error);
      setSortedData([]);
    } else if (data) {
      let normalized: any[] = [];

      if (Array.isArray(data)) {
        const groupedByOuterName = new Map<string, { kustoLink: string; innerMap: Map<string, any[]> }>();

        data.forEach((outer: any) => {
          const outerName = outer?.name || "";
          if (!outerName) return;
          const innerDataArray = Array.isArray(outer.data) ? outer.data : [];
          const kustoLink = outer.kustoLink || "";

          if (!groupedByOuterName.has(outerName)) {
            groupedByOuterName.set(outerName, { kustoLink, innerMap: new Map() });
          }

          const outerEntry = groupedByOuterName.get(outerName)!;

          innerDataArray.forEach((innerItem: any) => {
            const innerName = innerItem?.name || "";
            if (!innerName) return;
            // When Daily granularity is selected, include Daily and Weekly data
            // When Monthly granularity is selected, include only Monthly data
            let snapshots: any[] = [];
            if (aggregation === "Daily") {
              snapshots = Array.isArray(innerItem.data)
                ? innerItem.data.filter((s: any) => s?.aggregationType === "Daily")
                : [];
            } else {
              snapshots = Array.isArray(innerItem.data)
                ? innerItem.data.filter((s: any) => s?.aggregationType === aggregation)
                : [];
            }
            if (!snapshots || snapshots.length === 0) return;

            if (!outerEntry.innerMap.has(innerName)) {
              outerEntry.innerMap.set(innerName, []);
            }
            outerEntry.innerMap.get(innerName)!.push(...snapshots);
          });
        });

        normalized = Array.from(groupedByOuterName.entries()).map(([outerName, { kustoLink, innerMap }]) => ({
          name: outerName,
          data: Array.from(innerMap.entries()).map(([innerName, snapshots]) => ({ name: innerName, data: snapshots })),
          kustoLink
        }));
      }

      setSortedData(arrSortDescByKeyLength({ key: "data", data: normalized } as IArrayKey));
    }
  }, [data, isFetching, error, aggregation]);

  return (
    <>
      {/* Loader while fetching new data */}
      <Loader loading={isFetching} rowProps={undefined} spinnerStack={undefined} />

      {/* Error Handling */}
      {error && <DisplayAPIsException height={250} />}

      {/* No Data Found */}
      {!isFetching && sortedData.length === 0 && (
        <span style={{ marginLeft: "45%", marginTop: 50, background: "#FFF", lineHeight: 6 }}>
          No Feature Data found for the given Tenant Id
        </span>
      )}

      {/* Aggregation selector + Render Data */}
      {!isFetching && sortedData.length > 0 ? (
        <div ref={containerRef}>
          <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 8, marginLeft: 16 }}>
            <FluentProvider theme={webLightTheme}>
              <CustomDropdown
                label="Data Granularity"
                data={aggregationOptions}
                defaultOption={{ optionText: aggregation, optionValue: aggregation, selectedOptions: [aggregation] }}
                onSelectedOption={handleSelectedAggregation}
                styles={{ root: { minWidth: 140 } }}
              />
            </FluentProvider>
          </div>
          <Card>
            <CardContent>
              <CustomerFeatureChart
                granularity={aggregation}
                carouseldata={{
                  name: "",
                  items: sortedData
                    .filter((item) => Array.isArray(item.data) && item.data.length > 0)
                    .map(({ name: kpiName, data: kpiData, kustoLink: kpiKustoLink }) => ({
                      name: kpiName || "",
                      data: kpiData || [],
                      kustoLink: kpiKustoLink || ""
                    }))
                }}
              />
            </CardContent>
          </Card>
        </div>
      ) : null}
    </>
  );
}
